import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Form, Spinner, Alert } from 'react-bootstrap';
import { formatDateLocalEnhanced } from '../../utils/dateUtils';
import { createPanelTransport, findPanelRecord, serializePanelSave } from './panelSizingRequests';
import { selectApiUrls, selectUpdatedApis } from '../../redux/slices/apiUrlSlice';
import { selectUserDetails, selectUserRoles } from '../../redux/slices/userSlice';
import { getProjectId, getProjectStatus, normalizeId } from '../../utils/dataNormalization';

// Model number patterns for Table 1 (stable constants, defined at module scope)
const REQUIRED_PATTERN = '7C';
const EXCLUDED_PATTERNS = ['RET', '5000', 'AGVT'];

const getPanelSizingRecord = (panelSizingData, job) => {
  const projectId = getProjectId(job);
  const normalizedProjectId = normalizeId(projectId);
  return panelSizingData[projectId] || panelSizingData[normalizedProjectId] || {};
};

/**
 * UpcomingReview Component - Tab 1: Upcoming Review
 * Displays two tables of jobs pending panel sizing review (Status = null).
 * Table 1 filters for 7C model numbers (excluding RET, 5000, AGVT).
 * Table 2 shows all matching jobs regardless of model number.
 * Status field is editable so users can assign a status and move items to WIP.
 */
function UpcomingReview({
  generalProjectData = [],
  panelSizingData = {},
  loading = false,
  error = null,
  onDataUpdate = null
}) {
  const dispatch = useDispatch();
  const transport = createPanelTransport(dispatch);
  const apiUrls = useSelector(selectApiUrls);
  const updatedApis = useSelector(selectUpdatedApis);
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const { options } = useSelector((state) => state.dropdown);

  const currentUser = userDetails?.name || userDetails?.displayName || 'Unknown';
  const primaryRole = userRoles?.length > 0 ? userRoles[0] : 'User';
  const isAdmin = Array.isArray(userRoles) && userRoles.some((role) => String(role).toLowerCase() === 'admin');

  const [sortConfig1, setSortConfig1] = useState({ key: 'ElectricalDue', direction: 'ascending' });
  const [searchTerm2, setSearchTerm2] = useState('');
  const [sortConfig2, setSortConfig2] = useState({ key: 'ElectricalDue', direction: 'ascending' });

  const [editedValues, setEditedValues] = useState({});
  const [savingFields, setSavingFields] = useState({});
  const [editingFields, setEditingFields] = useState({});
  useRealtimeDraft(Object.values(editingFields).some(Boolean) || Object.values(savingFields).some(Boolean) || Object.keys(editedValues).length > 0);

  const isValueInDropdown = (value, dropdownOptions) =>
    dropdownOptions.some((opt) => opt.DisplayText === value || opt.displayName === value);

  const statusOptions =
    options?.filter((opt) => opt.DropdownType === 'Panel Review Status').sort((a, b) => a.SortOrder - b.SortOrder) || [];

  // Table 1: also requires ModelNumber contains "7C" but NOT "RET", "5000", "AGVT"
  const table1Data = useMemo(() => {
    return generalProjectData
      .filter((job) => {
        const panelSizing = getPanelSizingRecord(panelSizingData, job);
        if (job.JobStatus === 'Shipped' || job.JobStatus === 'Ready to Schedule') return false;
        if (getProjectStatus(job) === 'TBD') return false;
        if (job.ElectricalDone) return false;
        if (job.ElectricalDue == null) return false;
        if (job.HideFromList !== null) return false;
        if (job.BookingLogOnly !== null) return false;
        if (job.ScheduleOnly !== null) return false;
        if (panelSizing.Status) return false;
        const model = job.ModelNumber || '';
        if (!model.includes(REQUIRED_PATTERN)) return false;
        if (EXCLUDED_PATTERNS.some((p) => model.includes(p))) return false;
        return true;
      })
        .map((job) => ({ ...job, Status: getPanelSizingRecord(panelSizingData, job)?.Status || '' }));
      }, [generalProjectData, panelSizingData]);

  const table1Ids = useMemo(() => new Set(table1Data.map((job) => normalizeId(getProjectId(job)))), [table1Data]);

  // Table 2: no model number restriction and excludes items already shown in 7C table
  // When searching, use broader matching and bypass most baseline filters
  const table2Data = useMemo(() => {
    const search = searchTerm2.trim().toLowerCase();
    const hasSearch = search.length > 0;

    return generalProjectData
      .filter((job) => {
        if (table1Ids.has(normalizeId(getProjectId(job)))) return false;

        if (job.JobStatus === 'Shipped') return false;
        if (job.HideFromList !== null) return false;
        if (job.ScheduleOnly !== null) return false;
        if (job.BookingLogOnly !== null) return false;

        if (hasSearch) {
          return (
            (job.JobNumber || '').toLowerCase().includes(search) ||
            (job.ProjectName || '').toLowerCase().includes(search) ||
            (job.ModelNumber || '').toLowerCase().includes(search)
          );
        }

        const panelSizing = getPanelSizingRecord(panelSizingData, job);
        if (job.JobStatus === 'Shipped' || job.JobStatus === 'Ready to Schedule') return false;
        if (getProjectStatus(job) === 'TBD') return false;
        if (job.ElectricalDone) return false;
        if (job.ElectricalDue == null) return false;
        if (job.HideFromList !== null) return false;
        if (job.BookingLogOnly !== null) return false;
        if (job.ScheduleOnly !== null) return false;
        if (panelSizing.Status) return false;
        return true;
      })
        .map((job) => ({ ...job, Status: getPanelSizingRecord(panelSizingData, job)?.Status || '' }));
      }, [generalProjectData, panelSizingData, searchTerm2, table1Ids]);

  const sortData = (data, cfg) =>
    [...data].sort((a, b) => {
      const av = a[cfg.key];
      const bv = b[cfg.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (cfg.key === 'ElectricalDue' || cfg.key === 'ControlPanelDue') {
        return cfg.direction === 'ascending' ? new Date(av) - new Date(bv) : new Date(bv) - new Date(av);
      }
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return cfg.direction === 'ascending' ? -1 : 1;
      if (as > bs) return cfg.direction === 'ascending' ? 1 : -1;
      return 0;
    });

  const sortedTable1 = useMemo(() => sortData(table1Data, sortConfig1), [table1Data, sortConfig1]);
  const sortedTable2 = useMemo(() => sortData(table2Data, sortConfig2), [table2Data, sortConfig2]);
  const hasSearch2 = searchTerm2.trim().length > 0;

  const makeHandleSort = (setSortConfig) => (key) =>
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending'
    }));

  const getSortIndicator = (cfg, key) => {
    if (cfg.key !== key) return null;
    return cfg.direction === 'ascending' ? ' ▲' : ' ▼';
  };

  // -------- Status field editing (same save pattern as SchematicsReview) --------

  const getFieldValue = (projectId, originalValue) => {
    if (editedValues[projectId] !== undefined) return editedValues[projectId];
    return originalValue || '';
  };

  const recordChangeLog = async (projectId, jobNumber, jobName, prevValue, newValue) => {
    if (!apiUrls.panelSizingChangeLog) return;
    try {
      await transport.post(apiUrls.panelSizingChangeLog, {
        projectID: projectId,
        currentUser,
        userRole: primaryRole,
        jobNumber: jobNumber || '',
        jobName: jobName || '',
        field: 'Status',
        prevValue: prevValue === null || prevValue === undefined ? '' : String(prevValue),
        newValue: newValue === null || newValue === undefined ? '' : String(newValue),
        dateTime: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error recording change log:', err);
    }
  };

  const handleFieldChange = (projectId, value) => {
    setEditedValues((prev) => ({ ...prev, [projectId]: value }));
  };

  const handleFieldBlur = (projectId, value, jobNumber, jobName) => serializePanelSave(
    dispatch, JSON.stringify([apiUrls.panelSizing, userDetails?.localAccountId, projectId]), async (assertCurrent) => {
    const fieldKey = `${projectId}_Status`;
    // The queued save compares against the fresh server record.
    setSavingFields((prev) => ({ ...prev, [fieldKey]: true }));

    try {
      assertCurrent();
      const panelSizingUrl = apiUrls.panelSizing;
      const isUpdatedApi = updatedApis.panelSizing;

      const existingRecord = await findPanelRecord(dispatch, `${panelSizingUrl}?projectId=${projectId}`, projectId);
      assertCurrent();
      if (String(existingRecord?.Status ?? '') === String(value ?? '')) {
        setEditingFields(prev => { const next = { ...prev }; delete next[fieldKey]; return next; });
        setEditedValues(prev => { const next = { ...prev }; delete next[projectId]; return next; });
        return;
      }

      if (existingRecord?.ID) {
        const url = isUpdatedApi
          ? `${panelSizingUrl}?projectId=${projectId}`
          : `${panelSizingUrl}/${existingRecord.ID}`;
        await transport.patch(url, { ID: existingRecord.ID, ProjectID: projectId, Status: value });
        await recordChangeLog(projectId, jobNumber, jobName, existingRecord.Status, value);
      } else {
        const url = isUpdatedApi ? `${panelSizingUrl}?projectID=${projectId}` : panelSizingUrl;
        await transport.post(url, { projectID: projectId, status: value });
        await recordChangeLog(projectId, jobNumber, jobName, '', value);
      }

      if (onDataUpdate) await onDataUpdate();

      setEditedValues((prev) => { const u = { ...prev }; delete u[projectId]; return u; });
      setEditingFields((prev) => { const u = { ...prev }; delete u[fieldKey]; return u; });
    } catch (err) {
      console.error('Error saving Status:', err);
      alert(`Failed to save Status: ${err.response?.data?.message || err.message}`);
    } finally {
      setSavingFields((prev) => { const u = { ...prev }; delete u[fieldKey]; return u; });
    }
  });

  const renderEditableStatusField = (projectId, value, jobNumber, jobName) => {
    const fieldKey = `${projectId}_Status`;
    const isSaving = savingFields[fieldKey];
    const isEditing = editingFields[fieldKey];
    const currentValue = getFieldValue(projectId, value);

    const handleDropdownChange = (e) => {
      const newValue = e.target.value;
      handleFieldChange(projectId, newValue);
      handleFieldBlur(projectId, newValue, jobNumber, jobName);
    };

    if (isEditing) {
      return (
        <Form.Select
          size="sm"
          value={currentValue}
          onChange={handleDropdownChange}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setEditedValues((prev) => { const u = { ...prev }; delete u[projectId]; return u; });
              setEditingFields((prev) => { const u = { ...prev }; delete u[fieldKey]; return u; });
            }
          }}
          disabled={isSaving}
          autoFocus
          style={{
            minWidth: '120px',
            backgroundColor: isSaving ? '#f8f9fa' : 'white',
            fontSize: '0.875rem'
          }}
        >
          <option value="">Select...</option>
          {statusOptions.map((opt) => (
            <option key={opt.ID} value={opt.DisplayText || opt.displayName}>
              {opt.DisplayText || opt.displayName}
            </option>
          ))}
        </Form.Select>
      );
    }

    const isValid = !currentValue || isValueInDropdown(currentValue, statusOptions);

    return (
      <div
        onDoubleClick={() => setEditingFields((prev) => ({ ...prev, [fieldKey]: true }))}
        style={{
          cursor: 'pointer',
          padding: '4px 8px',
          minHeight: '24px',
          borderRadius: '4px',
          transition: 'background-color 0.2s',
          color: isValid ? 'inherit' : '#dc3545',
          fontWeight: isValid ? 'normal' : 'bold'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        title={isValid ? 'Double-click to edit' : `Invalid value: ${currentValue}`}
      >
        {currentValue || '-'}
      </div>
    );
  };

  // -------- Shared table rendering --------

  const renderHeaders = (cfg, handleSort) => (
    <tr>
      {[
        ['JobNumber', 'Job Number'],
        ['ProjectName', 'Project Name'],
        ['ModelNumber', 'Model Number'],
        ['JobStatus', 'Job Status'],
        ['ElectricalEngineer', 'Electrical Engineer'],
        ['ElectricalDue', 'Electrical Due'],
        ['ControlPanelDue', 'Control Panel Due'],
        ['Status', 'Status']
      ].map(([key, label]) => (
        <th key={key} onClick={() => handleSort(key)} style={{ cursor: 'pointer', userSelect: 'none' }}>
          {label}{getSortIndicator(cfg, key)}
        </th>
      ))}
    </tr>
  );

  const renderRows = (data) => {
    if (data.length === 0) {
      return (
        <tr>
          <td colSpan="8" className="text-center text-muted py-4">No jobs found</td>
        </tr>
      );
    }
    return data.map((job) => (
      <tr key={getProjectId(job)}>
        <td>{job.JobNumber || '-'}</td>
        <td>{job.ProjectName || '-'}</td>
        <td>{job.ModelNumber || '-'}</td>
        <td>{job.JobStatus || '-'}</td>
        <td>{job.ElectricalEngineer || '-'}</td>
        <td>{job.ElectricalDue ? formatDateLocalEnhanced(job.ElectricalDue) : '-'}</td>
        <td>{job.ControlPanelDue ? formatDateLocalEnhanced(job.ControlPanelDue) : '-'}</td>
        <td>{renderEditableStatusField(getProjectId(job), job.Status, job.JobNumber, job.ProjectName)}</td>
      </tr>
    ));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner>
        <div className="mt-2">Loading data...</div>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  const handleSort1 = makeHandleSort(setSortConfig1);
  const handleSort2 = makeHandleSort(setSortConfig2);

  return (
    <div>
      {/* Table 1 - 7C Models */}
      <div className="mb-5">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h4>7C Models <span className="text-muted fs-6 fw-normal">(ModelNumber contains "7C", excludes RET / 5000 / AGVT)</span></h4>
          <span className="text-muted">{sortedTable1.length} job{sortedTable1.length !== 1 ? 's' : ''}</span>
        </div>
        {isAdmin && (
          <div className="border rounded p-2 mb-2 bg-light">
            <div className="fw-semibold mb-1">Applied filters (7C)</div>
            <div className="small text-muted">- JobStatus is not Shipped or Ready to Schedule</div>
            <div className="small text-muted">- ProjectStatus is not TBD</div>
            <div className="small text-muted">- ElectricalDone is false</div>
            <div className="small text-muted">- ElectricalDue is not null</div>
            <div className="small text-muted">- HideFromList, ScheduleOnly, BookingLogOnly are null</div>
            <div className="small text-muted">- PanelSizing.Status is null/empty</div>
            <div className="small text-muted">- ModelNumber contains 7C and excludes RET, 5000, AGVT</div>
          </div>
        )}
        <div className="table-responsive">
          <Table striped bordered hover size="sm">
            <thead>{renderHeaders(sortConfig1, handleSort1)}</thead>
            <tbody>{renderRows(sortedTable1)}</tbody>
          </Table>
        </div>
      </div>

      {/* Table 2 - All Upcoming */}
      <div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h4>All Upcoming <span className="text-muted fs-6 fw-normal">(all model numbers)</span></h4>
          <span className="text-muted">{sortedTable2.length} job{sortedTable2.length !== 1 ? 's' : ''}</span>
        </div>
        {isAdmin && (
          <div className="border rounded p-2 mb-2 bg-light">
            <div className="fw-semibold mb-1">Applied filters (All Upcoming)</div>
            <div className="small text-muted">- Excludes any item already shown in 7C</div>
            <div className="small text-muted">- JobStatus is not Shipped</div>
            <div className="small text-muted">- HideFromList, ScheduleOnly, BookingLogOnly are null</div>
            {hasSearch2 ? (
              <>
                <div className="small text-muted">- Search mode: match JobNumber, ProjectName, or ModelNumber</div>
                <div className="small text-muted">- Other baseline filters are bypassed while searching</div>
              </>
            ) : (
              <>
                <div className="small text-muted">- JobStatus is not Ready to Schedule</div>
                <div className="small text-muted">- ProjectStatus is not TBD</div>
                <div className="small text-muted">- ElectricalDone is false</div>
                <div className="small text-muted">- ElectricalDue is not null</div>
                <div className="small text-muted">- PanelSizing.Status is null/empty</div>
              </>
            )}
          </div>
        )}
        <Form.Control
          type="text"
          placeholder="Search by Job Number, Project Name, or Model Number..."
          value={searchTerm2}
          onChange={(e) => setSearchTerm2(e.target.value)}
          className="mb-2"
          style={{ maxWidth: '450px' }}
        />
        <div className="table-responsive">
          <Table striped bordered hover size="sm">
            <thead>{renderHeaders(sortConfig2, handleSort2)}</thead>
            <tbody>{renderRows(sortedTable2)}</tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}

export default UpcomingReview;
